<template>
  <v-container fluid>
    <v-card class="mx-auto mt-5" outlined tile>
      <v-toolbar flat>
        <v-text-field
          label="Search"
          type="text"
          prepend-icon="search"
          v-model="search"
          clearable
          hide-details
        ></v-text-field>
        <v-select
          label="Search by"
          v-model="searchArg"
          :items="searchArgs"
          hide-details
          return-object
          class="mx-2"
        ></v-select>
        <v-select
          label="Active"
          v-model="filterIsActive"
          :items="filterIsActiveItems"
          hide-details
        />

        <v-spacer></v-spacer>

        <v-btn text color="primary" @click="add">
          <v-icon>add</v-icon>
          Add Person
        </v-btn>
      </v-toolbar>

      <v-expansion-panels flat v-model="expandAdditionalFilters">
        <v-expansion-panel>
          <v-expansion-panel-header>Additional Filters ({{ additionalFilters.length }})</v-expansion-panel-header>
          <v-expansion-panel-content>
            <filter-builder-form
              v-model="additionalFilters"
              :fields="additionalFilterFields"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <person-list
        v-bubble.error
        v-bubble.info
        @open="onOpen"
        @open:duplicates="duplicatesDialog = true; duplicatesDialogItem = $event"
        :filters="filters"
      />
    </v-card>

    <v-dialog
      v-model="dialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          Add Person
        </v-card-title>

        <v-card-text>
          <person-form
            v-model="form"
            @valid="valid = $event"
          />
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="save"
            :loading="saving"
            :disabled="saving || !valid"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="dialog = false"
            :disabled="saving"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="duplicatesDialog"
      max-width="1800"
    >
      <v-card min-height="90vh">
        <v-card-title>
          Detected Duplicates
        </v-card-title>

        <v-card-text>
          <person-duplicate-list
            v-if="duplicatesDialog && duplicatesDialogItem"
            :personId="duplicatesDialogItem._id"
            v-bubble.error
            v-bubble.info
            @close="duplicatesDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import PersonForm from '../components/forms/PersonForm.vue'
import PersonList from '../components/PersonList.vue'
import PersonDuplicateList from '../components/PersonDuplicateList.vue'
import FilterBuilderForm from '../components/forms/FilterBuilderForm.vue'
import { set } from 'lodash'
import { subYears, subMonths, startOfDay } from 'date-fns'

export default {
  name: 'AdminPersonListView',

  components: {
    PersonList,
    PersonForm,
    PersonDuplicateList,
    FilterBuilderForm
  },

  data: () => ({
    search: '',
    searchArg: { value: 'nameLike', text: 'Name' },
    searchArgs: [
      { value: 'nameLike', text: 'Name' },
      { value: 'emailLike', text: 'Email' },
      { value: 'phoneNumberLike', text: 'Phone Number' },
      { value: 'personId', text: 'MSA Person ID', type: Number }
    ],
    dialog: false,
    form: {},
    valid: false,
    saving: false,
    filterIsActive: 'ACTIVE_ANY',
    filterIsActiveItems: [
      { value: 'ALL', text: 'All' },
      { value: 'ACTIVE_MONTH', text: 'Active (1 month)' },
      { value: 'ACTIVE_YEAR', text: 'Active (12 months)' },
      { value: 'ACTIVE_ANY', text: 'Active (all time)' },
      { value: 'INACTIVE', text: 'Inactive' }
    ],
    duplicatesDialog: false,
    duplicatesDialogItem: null,
    additionalFilters: [],
    additionalFilterFields: [
      { text: 'Name', value: 'name', like: true, multiple: false },
      { text: 'Given Name', value: 'givenName' },
      { text: 'Family Name', value: 'familyName' },
      { text: 'Email Address', value: 'email', like: true, multiple: false },
      { text: 'Phone Number', value: 'phoneNumber', like: true, multiple: false },
      { text: 'MSA Person ID', value: 'personId', type: Number },
      { text: 'USI', value: 'uniqueStudentIdentifier' },
      { text: 'System ID', value: '_id' },
      { text: 'InfoSys Trainee ID', value: 'infosysTraineeId', type: Number, gt: true, lt: true, gte: true, lte: true },
      { text: 'InfoSys Member ID', value: 'infosysMemberId', type: Number, gt: true, lt: true, gte: true, lte: true },
      { text: 'InfoSys Person ID', value: 'infosysPersonId', type: Number, gt: true, lt: true, gte: true, lte: true },
      { text: 'Last Login', value: 'lastLoggedInAt', type: Date, eq: false, ne: false, gt: true, lt: true, gte: true, lte: true }
    ],
    expandAdditionalFilters: null
  }),

  computed: {
    filters () {
      const filters = {}
      const today = startOfDay(new Date())

      if (this.search) {
        filters[this.searchArg.value] = (this.searchArg.type || String)(this.search)
      }

      if (this.filterIsActive !== 'ALL') {
        filters.isActive = this.filterIsActive !== 'INACTIVE'

        const lastLoggedInAt = (this.filterIsActive === 'ACTIVE_MONTH')
          ? subMonths(today, 1)
          : (this.filterIsActive === 'ACTIVE_YEAR')
            ? subYears(today, 1)
            : null

        if (lastLoggedInAt) {
          set(filters, '_operators.lastLoggedInAt', { gte: lastLoggedInAt })
        }
      }

      for (const filter of this.additionalFilters) {
        if (filter.operator === 'eq') {
          set(filters, ['AND', filter.field], filter.value)
        } else if (filter.operator === 'like') {
          set(filters, `${filter.field}Like`, filter.value)
        } else {
          set(filters, ['AND', '_operators', filter.field, filter.operator], filter.value)
        }
      }

      return filters
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    onOpen (item) {
      this.$router.push({ name: 'AdminPerson', params: { id: item._id } })
    },

    add () {
      this.form = {}
      this.dialog = true
    },

    async save () {
      this.saving = true
      try {
        const res = await this.$apollo.mutate({
          mutation: gql`mutation ($record: CreateOnePersonInput!) {
            personCreateOne(record: $record) {
              recordId
            }
          }`,
          variables: {
            record: this.form
          }
        })
        this.$router.push({ name: 'AdminPerson', params: { id: res.data.personCreateOne.recordId } })
      } catch (e) {
        this.onError(e)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
